import React from 'react'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
} from '@mui/material'
import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  User,
  Ticket,
  CreditCard,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

const SidebarItem = ({ icon: Icon, label, to, isActive, onClick }) => {
      return (
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to={to}
            onClick={onClick}
            sx={{
              backgroundColor: isActive ? 'primary.main' : 'transparent',
              color: isActive ? 'white' : 'grey.700',
              borderRadius: 2,
              margin: 0.5,
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                backgroundColor: isActive ? 'primary.dark' : 'grey.100',
                color: isActive ? 'white' : 'primary.main',
                transform: 'translateX(4px)',
              },
              '&::before': isActive ? {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                backgroundColor: 'secondary.main',
                borderRadius: '0 2px 2px 0',
              } : {},
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
              <Icon size={20} />
            </ListItemIcon>
            <ListItemText 
              primary={label} 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  fontWeight: isActive ? 700 : 500,
                  fontSize: '0.875rem',
                } 
              }} 
            />
          </ListItemButton>
        </ListItem>
      )
    }

export default function Sidebar({ sidebarCollapsed, toggleSidebar }) {
  const location = useLocation()
  
  const navigationItems = [
    { icon: Home, label: 'Home', to: '/showrunner/home', key: 'home' },
    { icon: User, label: 'Profile', to: '/showrunner/profile', key: 'profile' },
    { icon: Ticket, label: 'Events', to: '/showrunner/events', key: 'events' },
    { icon: CreditCard, label: 'Financial', to: '/showrunner/financial', key: 'financial' },
    { icon: Users, label: 'Orders', to: '/showrunner/orders', key: 'orders' },
    // { icon: Users, label: 'Referrals', to: '/showrunner/slickfilliates', key: 'slickfilliates' },
    { icon: Settings, label: 'Admin', to: '/showrunner/slicktix', key: 'slicktix' },
  ]
  
  const getCurrentTab = () => {
    const path = location.pathname.split('/showrunner/')[1]
    return path || 'home'
  }
  
  return (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            background: 'linear-gradient(180deg, #ffffff 0%, #E8F0FF 100%)',
            borderRight: '3px solid',
            borderImage: 'linear-gradient(180deg, #4A5BFF 0%, #00C851 100%) 1',
            transition: 'width 0.2s ease-in-out',
            width: sidebarCollapsed ? '70px' : '280px',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '8px 0 30px rgba(0, 41, 255, 0.08)',
          }}
        >
      {/* Header */}
      <Box
        sx={{
          padding: 2,
          borderBottom: '2px solid',
          borderColor: 'primary.light',
          background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 4px 12px rgba(0, 41, 255, 0.2)',
        }}
      >
        {!sidebarCollapsed && (
          <Typography variant="h6" color="white" fontWeight="bold" sx={{ 
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          }}>
            🎭 SlickTix
          </Typography>
        )}
        <IconButton
          onClick={toggleSidebar}
          size="small"
          sx={{ 
            padding: 1,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
            }
          }}
        >
          {sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </IconButton>
      </Box>
      
      {/* Navigation */}
      <Box sx={{ flex: 1, padding: 1 }}>
        <List>
          {navigationItems.map((item) => (
            <SidebarItem
              key={item.key}
              icon={item.icon}
              label={sidebarCollapsed ? '' : item.label}
              to={item.to}
              isActive={getCurrentTab() === item.key}
            />
          ))}
        </List>
      </Box>
      
      {/* Footer */}
      {!sidebarCollapsed && (
        <>
          <Divider />
          <Box sx={{ padding: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="grey.500">
              © 2024 SlickTix
            </Typography>
          </Box>
        </>
      )}
    </Box>
  )
}
